import { Link, useLocation } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { routes } from '@/routes';
import { getLocaleFromPath } from '@/lib/publicBaseUrl';
import { getNextIntake } from '@/data/cohorts';
import { products, isZarRegion } from '@/data/pricing';
import { useRegion } from '@/contexts/RegionContext';
import { Price } from '@/components/ui/Price';
import { SocialShare } from '@/components/ui/SocialShare';
import { useTranslation } from 'react-i18next';

export const TeamFacilitatorCertificationPage = () => {
  const { t } = useTranslation();
  const nextCohort = getNextIntake(2);
  const product = products['team-facilitator-certification'];
  const { regionCode, locale } = useRegion();
  const location = useLocation();
  const currentLocale = getLocaleFromPath(location.pathname);
  const routeConfig = routes.find(r => r.path === '/certification/team-facilitator');
  const localeSeo = currentLocale && routeConfig?.seo.byLocale ? (routeConfig.seo.byLocale as any)[currentLocale] : undefined;
  const pageTitle = localeSeo?.title || routeConfig?.seo.title || 'Enneagram Team Facilitator Certification | FIVE LENS';

  const isZar = isZarRegion(regionCode, locale);

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "FIVE LENS Team Facilitator Certification",
    "description": "Certification programme for facilitating team-building sessions using Five Lens modules on trust, feedback and collaboration. Includes access to a library of customisable Team Skills Modules.",
    "provider": {
      "@type": "Organization",
      "name": "FIVE LENS Enneagram",
      "url": "https://www.fivelensenneagram.com"
    },
    "educationalCredentialAwarded": "FIVE LENS Team Facilitator Certification",
    "timeRequired": "P4W",
    "courseMode": ["online", "blended"],
    "inLanguage": "en",
    "offers": [
      {
        "@type": "Offer",
        "price": "18000",
        "priceCurrency": "ZAR",
        "availability": "https://schema.org/InStock",
        "url": "https://www.fivelensenneagram.com/certification/team-facilitator"
      },
      {
        "@type": "Offer",
        "price": "1350",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock",
        "url": "https://www.fivelensenneagram.com/certification/team-facilitator"
      },
      {
        "@type": "Offer",
        "price": "1800",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://www.fivelensenneagram.com/certification/team-facilitator"
      }
    ],
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "8-10 hours pre-course online + 4 x 2-3 hour live Zoom sessions"
    }
  };

  return (
    <main>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      </Helmet>
      <SocialShare title={pageTitle} />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-8">
        <Breadcrumb items={[{ label: 'For Coaches and Practitioners', href: '/practitioners' }, { label: 'Team Facilitator Certification' }]} />
      </div>
      <section className="pt-32 pb-16 px-6 max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-serif text-ink mb-4">
          {t('teamFacilitator.h1', 'FIVE LENS Enneagram Team Facilitator Certification (Level 2)')}
        </h1>
        <p className="text-xl text-ink/80 mb-4 max-w-3xl leading-relaxed">
          {t('teamFacilitator.subheading', 'Facilitate team sessions using modules on trust, feedback and collaboration.')}
        </p>
        <h2 className="text-base text-ink/60 mb-8 max-w-3xl font-light">
          For team coaches, facilitators and in-house L&D professionals.
        </h2>
        
        <div className="bg-white p-8 border border-ground-deep shadow-sm mb-16">
          <h3 className="text-sm font-bold tracking-widest uppercase text-rule-3 mb-2">Next Cohort</h3>
          <p className="text-2xl font-serif text-ink mb-2">
            {nextCohort ? nextCohort.displayDate : 'Next intake: scheduled on request'}
          </p>
          <p className="text-ink/70 mb-6">
            {nextCohort
              ? nextCohort.seatsAvailable != null && nextCohort.seatsTotal != null
                ? `${nextCohort.seatsAvailable} of ${nextCohort.seatsTotal} seats remaining.`
                : ''
              : 'Intakes are scheduled on request and confirmed once a cohort is formed.'}
          </p>

          <div className="mb-6 space-y-4 text-ink/80 text-sm max-w-2xl border-t border-b border-ground-deep py-4">
            <p>
              <span className="font-semibold text-ink">Format:</span> 8 to 10 hours pre-course online. 4 live Zoom sessions of 2 to 3 hours over 4 weeks. Case studies with a written assignment and presentations.
            </p>
            <p>
              <span className="font-semibold text-ink">Included:</span> A library of customisable Team Skills Modules, and a one-to-one preparation call with a FIVE LENS Enneagram Moderator for the first client process.
            </p>
          </div>

          {/* Prerequisite Notice directly above price */}
          <div className="mb-6 p-4 bg-ground border border-ground-deep border-l-4 border-l-rule-3 text-sm text-ink/90 leading-relaxed max-w-2xl">
            <span className="font-bold text-ink">{t('teamFacilitator.prereqLabel', 'Prerequisite:')}</span>{' '}
            {t('teamFacilitator.prereqBodyPart1', 'Level 2 requires the FIVE LENS Practitioner Certification (Level 1). New to FIVE LENS? Take both levels together in the')}{' '}
            <Link 
              to="/register?product=certification-bundle" 
              className="text-rule-3 font-semibold underline underline-offset-4 hover:text-ink transition-colors"
            >
              {t('teamFacilitator.bundleLinkText', 'Practitioner + Team Facilitator Bundle')}
            </Link>{' '}
            {t('teamFacilitator.prereqBodyPart2', 'and save.')}
          </div>

          <div className="font-bold text-xl text-ink mb-6">
            <Price productKey="team-facilitator-certification" />
          </div>

          {!nextCohort && (
            <div className="mb-6 p-4 bg-ground border border-ground-deep text-xs text-ink/90 leading-relaxed max-w-2xl">
              Your intake date will be confirmed within five working days of enrolment. If the confirmed date does not suit you, you may cancel for a full refund.
            </div>
          )}

          <div>
            <Link to="/register?product=team-facilitator-certification" className="inline-block bg-ink text-white px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-ink/70 transition-colors">
              {nextCohort ? 'Enrol in Next Cohort' : 'Enrol in Next Cohort'}
            </Link>
          </div>

          {/* Secondary text link below Enrol in Next Cohort */}
          <div className="mt-4">
            <Link
              to="/register?product=certification-bundle"
              className="text-xs uppercase tracking-widest text-ink/70 hover:text-rule-3 underline underline-offset-4 transition-colors font-medium inline-block"
            >
              {t('teamFacilitator.enrolBundleSecondary', 'Not yet Level 1 certified? Enrol in the bundle')}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};
